// Client-side wrapper for the worker's /api/immich/* routes. The IMMICH_API_KEY
// only lives on the worker — these helpers just shape requests and unpack
// responses for the React layer.
//
// See `worker/src/index.ts` for the matching server routes.

import { API_BASE } from './apiBase';

/** Subset of Immich's getAssetInfo response we round-trip through the
 *  worker so the client can persist a complete ImmichPhotoRef without a
 *  second metadata round-trip. */
export interface UploadInfo {
  originalFileName?: string;
  fileCreatedAt?: string;
  exifInfo?: {
    exifImageWidth?: number;
    exifImageHeight?: number;
    dateTimeOriginal?: string;
  };
}

export interface UploadResponse {
  assetId: string;
  albumId: string;
  status: 'created' | 'replaced' | 'duplicate';
  /** True when this upload caused the Worker to lazily create the album.
   *  Use this to decide whether to persist `immichAlbumId` on the trip. */
  albumCreated: boolean;
  info?: UploadInfo | null;
}

export interface UploadPhotoParams {
  /** The image / video bytes to upload. */
  file: File | Blob;
  /** Album-naming context — passed to the Worker so it can lazily create
   *  the album on first upload. The Worker ignores these if
   *  `immichAlbumId` is provided. */
  tripName: string;
  tripLocation: string;
  /** The URL the user uploaded from. Persisted in the album description so
   *  jumping from Immich back to the trip is one click. */
  tripUrl: string;
  /** Existing album to append to. When omitted, the Worker creates one. */
  immichAlbumId?: string;
  /** Defaults to the file's name. */
  fileName?: string;
  /** ISO 8601. Defaults to now on the worker side. */
  fileCreatedAt?: string;
  /** Immich user IDs of the trip's current collaborators. Only consumed
   *  by the worker when it has to create the album (i.e. no
   *  `immichAlbumId` passed) — those users get shared editor access on
   *  the freshly-created album. For uploads to an existing album the
   *  client manages shares separately via addAlbumCollaborators. */
  collaboratorUserIds?: ReadonlyArray<string>;
}

/** Upload a single photo to the trip's Immich album. The worker creates the
 *  album lazily on the first upload — when `albumCreated` is true on the
 *  response, the caller MUST persist `albumId` on the trip via
 *  `setTripImmichAlbumId` so subsequent uploads append to the same album
 *  instead of creating a fresh one each time. */
export const uploadPhotoToImmich = async (params: UploadPhotoParams): Promise<UploadResponse> => {
  const form = new FormData();
  // The `file` field is what the worker checks via `instanceof Blob`; the
  // optional `fileName` argument falls through to the multipart entry's
  // name attribute when File is supported.
  form.set(
    'file',
    params.file,
    params.fileName ?? (params.file instanceof File ? params.file.name : 'upload.jpg'),
  );
  form.set('tripName', params.tripName);
  form.set('tripLocation', params.tripLocation);
  form.set('tripUrl', params.tripUrl);
  if (params.immichAlbumId) form.set('immichAlbumId', params.immichAlbumId);
  if (params.fileName) form.set('fileName', params.fileName);
  if (params.fileCreatedAt) form.set('fileCreatedAt', params.fileCreatedAt);
  if (params.collaboratorUserIds && params.collaboratorUserIds.length > 0) {
    form.set('collaboratorUserIds', params.collaboratorUserIds.join(','));
  }

  const response = await fetch(`${API_BASE}/api/immich/upload`, {
    method: 'POST',
    body: form,
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`uploadPhotoToImmich failed: ${response.status} ${body}`);
  }
  return (await response.json()) as UploadResponse;
};

/** Drop one or more assets from a trip's album. The asset itself stays in
 *  the user's Immich library — only the album-membership is removed. */
export const removePhotosFromAlbum = async (
  albumId: string,
  assetIds: ReadonlyArray<string>,
): Promise<void> => {
  if (assetIds.length === 0) return;
  const response = await fetch(
    `${API_BASE}/api/immich/album/${encodeURIComponent(albumId)}/remove-assets`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetIds: [...assetIds] }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`removePhotosFromAlbum failed: ${response.status} ${body}`);
  }
};

export interface ArchiveAlbumParams {
  albumId: string;
  /** Album name before archive (we suffix this with " (unlinked MM/DD/YYYY)"). */
  originalAlbumName: string;
  /** Location to keep as the album description (the trip URL line is dropped). */
  tripLocation: string;
  /** Defaults to `new Date()`. */
  unlinkedAt?: Date;
}

/** Mark a trip's Immich album as no longer linked to a live trip. The album
 *  and its photos remain in the user's library; only the metadata changes:
 *    - name: original + " (unlinked MM/DD/YYYY)"
 *    - description: just the location (Trippy URL line stripped)
 *  Fire-and-forget on trip-delete. */
export const archiveTripAlbum = async (params: ArchiveAlbumParams): Promise<void> => {
  const at = params.unlinkedAt ?? new Date();
  const stamp = `${(at.getMonth() + 1).toString().padStart(2, '0')}/${at
    .getDate()
    .toString()
    .padStart(2, '0')}/${at.getFullYear()}`;
  const albumName = `${params.originalAlbumName} (unlinked ${stamp})`;
  const description = params.tripLocation;
  const response = await fetch(
    `${API_BASE}/api/immich/album/${encodeURIComponent(params.albumId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ albumName, description }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`archiveTripAlbum failed: ${response.status} ${body}`);
  }
};

// ── Collaborators ──────────────────────────────────────────────────────────

export interface ImmichCollaboratorLookup {
  email: string;
  userId: string;
  name?: string;
}

export interface LookupEmailsResponse {
  found: Array<ImmichCollaboratorLookup>;
  notFound: Array<string>;
}

/** Pre-flight validation — resolves each email to an Immich user (or
 *  flags it as notFound). Used to gate "add collaborator" attempts so
 *  we never persist an email that isn't backed by an Immich account. */
export const lookupImmichUsersByEmail = async (
  emails: ReadonlyArray<string>,
): Promise<LookupEmailsResponse> => {
  if (emails.length === 0) return { found: [], notFound: [] };
  const response = await fetch(`${API_BASE}/api/immich/users/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails: [...emails] }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`lookupImmichUsersByEmail failed: ${response.status} ${body}`);
  }
  return (await response.json()) as LookupEmailsResponse;
};

export interface AddAlbumCollaboratorsResponse {
  added: Array<{ email: string; userId: string; role: 'editor' }>;
  notFound: Array<string>;
}

/** Share an existing album with the given emails. Returns per-email
 *  outcomes so the client can persist the resolved userIds and surface
 *  any unmatched emails as inline errors. */
export const addAlbumCollaborators = async (
  albumId: string,
  emails: ReadonlyArray<string>,
  role: 'editor' = 'editor',
): Promise<AddAlbumCollaboratorsResponse> => {
  if (emails.length === 0) return { added: [], notFound: [] };
  const response = await fetch(
    `${API_BASE}/api/immich/album/${encodeURIComponent(albumId)}/collaborators`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails: [...emails], role }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`addAlbumCollaborators failed: ${response.status} ${body}`);
  }
  return (await response.json()) as AddAlbumCollaboratorsResponse;
};

/** Revoke album access for a single user. Idempotent at the API level
 *  — Immich treats "user not in album" as a no-op. */
export const removeAlbumCollaborator = async (
  albumId: string,
  immichUserId: string,
): Promise<void> => {
  const response = await fetch(
    `${API_BASE}/api/immich/album/${encodeURIComponent(albumId)}/collaborators/${encodeURIComponent(immichUserId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`removeAlbumCollaborator failed: ${response.status} ${body}`);
  }
};

/** Worker-proxied URL for an asset's thumbnail. Safe to drop straight into
 *  an `<img>` tag — the worker streams it with a year-long immutable cache
 *  header, so the browser handles repeats. */
export const immichThumbnailUrl = (
  assetId: string,
  size: 'preview' | 'thumbnail' = 'preview',
): string => `${API_BASE}/api/immich/asset/${encodeURIComponent(assetId)}/thumbnail?size=${size}`;

/** Worker-proxied URL for the original asset bytes. Used by the lightbox. */
export const immichOriginalUrl = (assetId: string): string =>
  `${API_BASE}/api/immich/asset/${encodeURIComponent(assetId)}/original`;
